export type HighlighterColor = "yellow" | "cyan" | "pink";

const HIGHLIGHTER_COLORS: Record<HighlighterColor, string> = {
  yellow: "rgba(255, 229, 83, 0.48)",
  cyan: "rgba(66, 231, 224, 0.44)",
  pink: "rgba(255, 105, 180, 0.44)",
};

type Point = { x: number; y: number };

export class ScreenPresentationController {
  private readonly screenVideo = document.createElement("video");
  private readonly annotationCanvas = document.createElement("canvas");
  private screenStream: MediaStream | undefined;
  private outputStream: MediaStream | undefined;
  private screenTrack: MediaStreamTrack | undefined;
  private outputTrack: MediaStreamTrack | undefined;
  private renderFrame = 0;
  private markerEnabled = false;
  private markerColor: HighlighterColor = "yellow";
  private drawingPointerId: number | undefined;
  private previousPoint: Point | undefined;
  private stopping = false;

  constructor(
    readonly canvas: HTMLCanvasElement,
    private readonly onNativeStop: () => void,
  ) {
    this.screenVideo.autoplay = true;
    this.screenVideo.muted = true;
    this.screenVideo.playsInline = true;
    this.canvas.addEventListener("pointerdown", this.onPointerDown);
    this.canvas.addEventListener("pointermove", this.onPointerMove);
    this.canvas.addEventListener("pointerup", this.onPointerUp);
    this.canvas.addEventListener("pointercancel", this.onPointerUp);
    this.render = this.render.bind(this);
  }

  async start(): Promise<MediaStreamTrack> {
    if (this.outputTrack) return this.outputTrack;
    let stream: MediaStream | undefined;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 24, max: 30 } },
        audio: false,
      });
      const track = stream.getVideoTracks()[0];
      if (!track) throw new Error("공유할 화면을 선택하지 않았습니다.");
      this.screenStream = stream;
      this.screenTrack = track;
      this.screenVideo.srcObject = stream;
      await this.screenVideo.play();
      await this.waitForMetadata();
      this.resizeCanvas(track);
      this.clearAnnotations();

      track.addEventListener("ended", this.onScreenTrackEnded, { once: true });
      this.renderFrame = requestAnimationFrame(this.render);
      this.outputStream = this.canvas.captureStream(24);
      this.outputTrack = this.outputStream.getVideoTracks()[0];
      if (!this.outputTrack) {
        throw new Error("화면 공유 영상을 만들지 못했습니다.");
      }
      this.outputTrack.contentHint = "detail";
      return this.outputTrack;
    } catch (error) {
      for (const track of stream?.getTracks() ?? []) track.stop();
      this.cleanup();
      throw error;
    }
  }

  stop(): void {
    this.stopping = true;
    for (const track of this.screenStream?.getTracks() ?? []) track.stop();
    for (const track of this.outputStream?.getTracks() ?? []) track.stop();
    this.cleanup();
    this.stopping = false;
  }

  isActive(): boolean {
    return Boolean(this.outputTrack);
  }

  getOutputTrack(): MediaStreamTrack | undefined {
    return this.outputTrack;
  }

  setMarkerEnabled(enabled: boolean): void {
    this.markerEnabled = enabled;
    this.canvas.classList.toggle("marker-enabled", enabled);
    if (!enabled) this.finishStroke();
  }

  isMarkerEnabled(): boolean {
    return this.markerEnabled;
  }

  setMarkerColor(color: HighlighterColor): void {
    this.markerColor = color;
  }

  getMarkerColor(): HighlighterColor {
    return this.markerColor;
  }

  clearAnnotations(): void {
    this.annotationCanvas
      .getContext("2d")
      ?.clearRect(0, 0, this.annotationCanvas.width, this.annotationCanvas.height);
  }

  private readonly onScreenTrackEnded = (): void => {
    if (!this.stopping) this.onNativeStop();
  };

  private waitForMetadata(): Promise<void> {
    if (this.screenVideo.readyState >= HTMLMediaElement.HAVE_METADATA) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.screenVideo.addEventListener("loadedmetadata", () => resolve(), {
        once: true,
      });
    });
  }

  private resizeCanvas(track: MediaStreamTrack): void {
    const settings = track.getSettings();
    const sourceWidth = settings.width ?? (this.screenVideo.videoWidth || 1920);
    const sourceHeight = settings.height ?? (this.screenVideo.videoHeight || 1080);
    const scale = Math.min(1, 1920 / Math.max(sourceWidth, sourceHeight));
    this.canvas.width = Math.max(2, Math.round((sourceWidth * scale) / 2) * 2);
    this.canvas.height = Math.max(2, Math.round((sourceHeight * scale) / 2) * 2);
    this.annotationCanvas.width = this.canvas.width;
    this.annotationCanvas.height = this.canvas.height;
  }

  private render(): void {
    const context = this.canvas.getContext("2d", { alpha: false });
    if (context && this.screenVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      context.drawImage(
        this.screenVideo,
        0,
        0,
        this.canvas.width,
        this.canvas.height,
      );
      context.drawImage(this.annotationCanvas, 0, 0);
    }
    this.renderFrame = requestAnimationFrame(this.render);
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (!this.markerEnabled || !event.isPrimary || event.button !== 0) return;
    const point = this.toCanvasPoint(event);
    if (!point) return;
    event.preventDefault();
    this.drawingPointerId = event.pointerId;
    this.previousPoint = point;
    this.canvas.setPointerCapture(event.pointerId);
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.drawingPointerId || !this.previousPoint) return;
    const point = this.toCanvasPoint(event);
    if (!point) return;
    event.preventDefault();
    const context = this.annotationCanvas.getContext("2d");
    if (!context) return;
    context.save();
    context.strokeStyle = HIGHLIGHTER_COLORS[this.markerColor];
    context.lineWidth = Math.max(
      10,
      Math.min(this.annotationCanvas.width, this.annotationCanvas.height) * 0.035,
    );
    context.lineCap = "round";
    context.lineJoin = "round";
    context.beginPath();
    context.moveTo(this.previousPoint.x, this.previousPoint.y);
    context.lineTo(point.x, point.y);
    context.stroke();
    context.restore();
    this.previousPoint = point;
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.drawingPointerId) return;
    event.preventDefault();
    this.finishStroke();
  };

  private finishStroke(): void {
    if (
      this.drawingPointerId !== undefined &&
      this.canvas.hasPointerCapture(this.drawingPointerId)
    ) {
      this.canvas.releasePointerCapture(this.drawingPointerId);
    }
    this.drawingPointerId = undefined;
    this.previousPoint = undefined;
  }

  private toCanvasPoint(event: PointerEvent): Point | undefined {
    const bounds = this.canvas.getBoundingClientRect();
    const scale = Math.min(
      bounds.width / this.canvas.width,
      bounds.height / this.canvas.height,
    );
    const renderedWidth = this.canvas.width * scale;
    const renderedHeight = this.canvas.height * scale;
    const offsetX = bounds.left + (bounds.width - renderedWidth) / 2;
    const offsetY = bounds.top + (bounds.height - renderedHeight) / 2;
    const x = (event.clientX - offsetX) / scale;
    const y = (event.clientY - offsetY) / scale;
    if (x < 0 || y < 0 || x > this.canvas.width || y > this.canvas.height) {
      return undefined;
    }
    return { x, y };
  }

  private cleanup(): void {
    cancelAnimationFrame(this.renderFrame);
    this.renderFrame = 0;
    this.finishStroke();
    this.setMarkerEnabled(false);
    this.screenVideo.srcObject = null;
    this.screenStream = undefined;
    this.outputStream = undefined;
    this.screenTrack = undefined;
    this.outputTrack = undefined;
  }
}
